import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faSpinner, faPlay, faLink, faXmark } from '@fortawesome/free-solid-svg-icons';

import api, { SERVER_URL } from '../../utils/api';
import MediaLightbox from '../MediaLightbox';
import { fileIcon } from './MessageBubble';
import { useRecentSearches } from '../RecentSearches';

/**
 * Media | Docs | Links — everything shared in this conversation, searchable.
 *
 * Reads GET /conversations/:id/shared, which derives the lists from the
 * messages themselves (same access, same "cleared for me" cut-off, nothing
 * from a deleted message), so this can never show something the thread
 * doesn't.
 *
 * Tasks are not a tab here: a direct chat has its own Tasks panel
 * (ChatTasksPanel), reached from the header or the Tasks button above.
 */

const TABS = [
    { key: 'media', label: 'Media', empty: 'No photos or videos shared yet.' },
    { key: 'docs', label: 'Docs', empty: 'No documents shared yet.' },
    { key: 'links', label: 'Links', empty: 'No links shared yet.' }
];

// A short first page so the panel opens fast; Load more then takes bigger bites.
const FIRST_PAGE = 10;
const MORE_PAGE = 20;


const absolute = (u) => (u?.startsWith('http') ? u : `${SERVER_URL}${u}`);

const shortDate = (iso) => new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
});

const hostOf = (href) => {
    try { return new URL(href).hostname.replace(/^www\./, ''); } catch (e) { return href; }
};

/**
 * client/path  where to ask. Staff read /conversations/:id/shared; the guest
 *              portal passes its own client and /chats/:id/shared, which
 *              answers in the same shape from the guest's history floor on.
 */
const SharedContent = ({ conversationId, client = api, path }) => {
    const [tab, setTab] = useState('media');
    const [query, setQuery] = useState('');
    const recentSearch = useRecentSearches('chat-shared', { value: query, onPick: setQuery });
    const [debounced, setDebounced] = useState('');
    // Tagged with the tab they were loaded for: the render right after a tab
    // switch still holds the previous tab's items, and a link row has no
    // attachment for the Media grid to read.
    const [loaded, setLoaded] = useState({ kind: 'media', items: [] });
    const [hasMore, setHasMore] = useState(false);
    const [nextBefore, setNextBefore] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lightbox, setLightbox] = useState(null);
    // Drops a slow response for a tab or search the person has already left.
    const requestId = useRef(0);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    const load = useCallback(async (before) => {
        const id = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            const res = await client.get(path || `/conversations/${conversationId}/shared`, {
                params: {
                    kind: tab,
                    q: debounced || undefined,
                    before: before || undefined,
                    limit: before ? MORE_PAGE : FIRST_PAGE
                }
            });
            if (id !== requestId.current) return;
            setLoaded((prev) => ({
                kind: tab,
                items: before && prev.kind === tab ? [...prev.items, ...res.data.items] : res.data.items
            }));
            setHasMore(res.data.hasMore);
            setNextBefore(res.data.nextBefore);
        } catch (err) {
            if (id !== requestId.current) return;
            setError(err.response?.data?.message || 'Could not load.');
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [conversationId, client, path, tab, debounced]);

    useEffect(() => {
        setLoaded({ kind: tab, items: [] });
        setLightbox(null);
        load(null);
    }, [load, tab]);

    const items = loaded.kind === tab ? loaded.items : [];
    const current = TABS.find((t) => t.key === tab) || TABS[0];
    const visual = tab === 'media'
        ? items.filter((it) => it.attachment).map((it) => ({ ...it.attachment, url: absolute(it.attachment.url) }))
        : [];

    return (
        <div className="msgr-info-section msgr-shared">
            <div className="msgr-shared-tabs" role="tablist">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        className={tab === t.key ? 'active' : ''}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="msgr-shared-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <input
                    {...recentSearch.inputProps}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={tab === 'links' ? 'Search links' : `Search ${current.label.toLowerCase()} by name`}
                    aria-label={`Search ${current.label}`}
                />
                {recentSearch.menu}
                {query && (
                    <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>

            {error && <p className="msgr-shared-empty">{error}</p>}

            {!error && !loading && items.length === 0 && (
                <p className="msgr-shared-empty">
                    {debounced ? `Nothing matches "${debounced}".` : current.empty}
                </p>
            )}

            {tab === 'media' && items.length > 0 && (
                <div className="msgr-shared-grid">
                    {items.map((it, i) => (
                        <button
                            key={it.key}
                            type="button"
                            className="msgr-shared-thumb"
                            onClick={() => { recentSearch.save(); setLightbox(i); }}
                            title={`${it.senderName} · ${shortDate(it.createdAt)}`}
                        >
                            {it.attachment.type === 'video' ? (
                                <>
                                    <video src={absolute(it.attachment.url)} preload="metadata" muted />
                                    <span className="msgr-shared-play"><FontAwesomeIcon icon={faPlay} /></span>
                                </>
                            ) : (
                                <img src={absolute(it.attachment.url)} alt={it.attachment.fileName || ''} loading="lazy" />
                            )}
                        </button>
                    ))}
                </div>
            )}

            {tab === 'docs' && items.map((it) => (
                <a
                    key={it.key}
                    className="msgr-shared-row"
                    href={absolute(it.attachment.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={it.attachment.fileName}
                    onClick={() => recentSearch.save()}
                >
                    <FontAwesomeIcon icon={fileIcon(it.attachment.fileName)} className="msgr-shared-icon" />
                    <span className="msgr-shared-body">
                        <span className="msgr-shared-title">{it.attachment.fileName || 'File'}</span>
                        <span className="msgr-shared-meta">{it.senderName} · {shortDate(it.createdAt)}</span>
                    </span>
                </a>
            ))}

            {tab === 'links' && items.map((it) => (
                <a
                    key={it.key}
                    className="msgr-shared-row"
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => recentSearch.save()}
                >
                    <FontAwesomeIcon icon={faLink} className="msgr-shared-icon" />
                    <span className="msgr-shared-body">
                        <span className="msgr-shared-title">{hostOf(it.url)}</span>
                        <span className="msgr-shared-link">{it.label}</span>
                        <span className="msgr-shared-meta">{it.senderName} · {shortDate(it.createdAt)}</span>
                    </span>
                </a>
            ))}

            {loading && (
                <p className="msgr-shared-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</p>
            )}

            {!loading && hasMore && (
                <button type="button" className="msgr-btn block" onClick={() => load(nextBefore)}>
                    Load more
                </button>
            )}

            {lightbox !== null && visual[lightbox] && (
                <MediaLightbox
                    items={visual}
                    index={lightbox}
                    onClose={() => setLightbox(null)}
                    onIndexChange={setLightbox}
                />
            )}
        </div>
    );
};

export default SharedContent;
